import { AuthService } from './authService';
import {
  ItemsDto,
  ItemsPagedDto,
  ItemsPagedQueryParams,
  PagedResult,
  CreateItemsRequest,
  UpdateItemsRequest,
  BulkUpdateItemsRequest,
  BulkDeleteItemsRequest,
  BulkItemOperationResponse
} from '../types/items';
import { AdvancedItemConfigDto, UpdateAdvancedItemConfigRequest, BinSuggestionResult, ItemMasterDataValidationDto } from '../types/advancedItemConfig';
import { ItemOverviewDto } from '../types/itemOverview';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemsService {
  
  async getCurrentItems(): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/items/current`);

    if (!response.ok) {
      throw new Error(`Failed to get current items: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get paginated items with server-side filtering, sorting, and enrichment.
   * Returns enriched items with BinGrp, MovementType, Report, and SortingBox descriptions.
   */
  async getCurrentItemsPaged(
    params: ItemsPagedQueryParams = {}
  ): Promise<PagedResult<ItemsPagedDto>> {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    // Sorting
    if (params.sortColumn) {
      queryParams.append('sortColumn', params.sortColumn);
    }
    if (params.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }

    // Filtering — generic loop for all filter fields (all strings)
    const filterFields: (keyof ItemsPagedQueryParams)[] = [
      'itemNo', 'description1', 'itemBarCode', 'supplierItemNo',
      'stdLocationName', 'stdBinCode', 'itemGrp1', 'itemGrp2', 'itemGrp3',
      'itemPictureName', 'currency', 'itemUnitCode', 'itemUnitTxt',
      'freeTxt1', 'freeTxt2', 'freeTxt3', 'freeTxt4', 'freeTxt5',
      'freeTxt6', 'freeTxt7', 'freeTxt8', 'freeTxt9', 'freeTxt10',
      'appFreeField1', 'appFreeField2',
      'internalFree1', 'internalFree2', 'internalFree3', 'internalFree4',
      'updatedBy', 'createdBy',
      'binGrpNo', 'itemBinSizeGroupID', 'itemType', 'pickRuleNo', 'movementTypeNo',
      'reportID', 'stcBoxTypeID', 'maxReplenishQty',
      'quantity', 'quantityReserved', 'quantityAvailable', 'purchasePrice', 'unitFactor',
      'lastUpdate', 'created',
      'putAwayToPickableBinYesNo', 'stcTransactionsYesNo', 'newLineYesNo',
      'useSharedBinYesNo', 'useMaxReplenishQtyYesNo',
      // B3: flervalgs lookup-filter (IN)
      'binGrpNos', 'itemBinSizeGroupIDs', 'itemTypes', 'pickRuleNos',
      'movementTypeNos', 'reportIDs', 'stcBoxTypeIDs',
    ];
    for (const field of filterFields) {
      const value = params[field];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(field, String(value));
      }
    }

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/items/current/paged${queryString ? `?${queryString}` : ''}`;

    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to get paged items'));
    }

    return await response.json();
  }

  async getItemsByCompany(companyNo: string): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/items/company/${companyNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get items for company ${companyNo}: ${response.status}`);
    }
    
    return await response.json();
  }

  async getItemByCompanyAndItemNo(companyNo: string, itemNo: string): Promise<ItemsDto> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/items/${companyNo}/${encodedItemNo}`);

    if (!response.ok) {
      throw new Error(`Failed to get item ${itemNo} for company ${companyNo}: ${response.status}`);
    }

    return await response.json();
  }

  async searchItems(companyNo: string, searchTerm: string): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search items: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Søk i selskapets vareregister. Med `warehouseId` begrenses treffene til varer som har
   * ItemBins-rad i lageret (uansett saldo) — brukes av operatørmoduler (TW 36386708).
   * Uten `warehouseId` søkes hele selskapets register (master-data-skjermer).
   */
  async searchCurrentItems(searchTerm: string, warehouseId?: string | null, maxResults?: number): Promise<ItemsDto[]> {
    const whParam = warehouseId ? `&warehouseId=${encodeURIComponent(warehouseId)}` : '';
    const maxParam = maxResults ? `&maxResults=${maxResults}` : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/search/current?searchTerm=${encodeURIComponent(searchTerm)}${whParam}${maxParam}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search current items: ${response.status}`);
    }
    
    return await response.json();
  }

  async checkItemExists(companyNo: string, itemNo: string): Promise<boolean> {
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/items/${companyNo}/${itemNo}`,
      { method: 'HEAD' }
    );
    
    return response.ok;
  }

  async searchByBarcode(barcode: string): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/barcode/current?barcode=${encodeURIComponent(barcode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search items by barcode: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Samlet, EKSAKT strekkodeoppslag (TW 36320099): matcher Items.ItemBarCode OG alternative
   * strekkoder i ItemUnits, med GTIN-13/14-varianter — i ETT kall. Foretrekk denne i skann-flyter
   * fremfor de sekvensielle searchByBarcode/itemUnits-kallene.
   */
  async resolveBarcode(barcode: string): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/resolve-barcode/current?barcode=${encodeURIComponent(barcode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to resolve barcode: ${response.status}`);
    }

    return await response.json();
  }

  async searchByBarcodeInCompany(companyNo: string, barcode: string): Promise<ItemsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/barcode/${companyNo}?barcode=${encodeURIComponent(barcode)}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search items by barcode in company: ${response.status}`);
    }
    
    return await response.json();
  }

  async getByBarcode(companyNo: string, barcode: string): Promise<ItemsDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/barcode/${companyNo}/${encodeURIComponent(barcode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item by barcode: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Varekort v2: vare + lagerstyring + saldo + åpne oppgaver + siste transaksjoner i ett kall.
   * Lagersjef/admin får i tillegg ordre, innkjøp, disponibelt og forbruk (server-side rolle).
   * Returnerer null når varen ikke finnes.
   */
  async getOverview(itemNo: string, warehouseId?: string | null): Promise<ItemOverviewDto | null> {
    const params = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/current/${encodeURIComponent(itemNo)}/overview${params}`
    );

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to get item overview: ${response.status}`);
    }

    return await response.json();
  }

  async getAdvancedConfig(companyNo: string, itemNo: string): Promise<AdvancedItemConfigDto | null> {
    try {
      const encodedItemNo = encodeURIComponent(itemNo);
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}/advanced-config`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to get advanced config: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async updateAdvancedConfig(
    companyNo: string,
    itemNo: string,
    request: UpdateAdvancedItemConfigRequest
  ): Promise<AdvancedItemConfigDto> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}/advanced-config`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update advanced config'));
    }

    return await response.json();
  }

  /**
   * Validate that an item has the master data required to start goods receiving
   * in an advanced warehouse (UseAWYesNo = 1): Items.BinGrpNo set, and either
   * Items.ItemBinSizeGroupID set or at least one ItemBinSize row.
   * Returns null if the item itself does not exist.
   */
  async validateMasterDataForReceiving(
    companyNo: string,
    itemNo: string
  ): Promise<ItemMasterDataValidationDto | null> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}/master-data-validation`
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to validate item master data: ${response.status}`);
    }

    return await response.json();
  }

  async suggestStandardBin(
    companyNo: string,
    itemNo: string,
    warehouseId?: string
  ): Promise<BinSuggestionResult> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const queryParams = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}/suggest-standard-bin${queryParams}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to suggest standard bin'));
    }

    return await response.json();
  }

  async suggestDedicatedBin(
    companyNo: string,
    itemNo: string,
    warehouseId?: string
  ): Promise<BinSuggestionResult> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const queryParams = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}/suggest-dedicated-bin${queryParams}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to suggest dedicated bin'));
    }

    return await response.json();
  }

  async createItem(request: CreateItemsRequest): Promise<ItemsDto> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/items`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to create item'));
    }

    return await response.json();
  }

  async updateItem(
    companyNo: string,
    itemNo: string,
    request: UpdateItemsRequest
  ): Promise<ItemsDto> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to update item'));
    }

    return await response.json();
  }

  async deleteItem(companyNo: string, itemNo: string): Promise<void> {
    const encodedItemNo = encodeURIComponent(itemNo);
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/items/${companyNo}/${encodedItemNo}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to delete item'));
    }
  }

  async bulkUpdateItems(request: BulkUpdateItemsRequest): Promise<BulkItemOperationResponse> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/items/bulk-update`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to bulk update items'));
    }

    return await response.json();
  }

  async bulkDeleteItems(request: BulkDeleteItemsRequest): Promise<BulkItemOperationResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/items/bulk-delete`,
      request
    );

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to bulk delete items'));
    }

    return await response.json();
  }
}

export const itemsService = new ItemsService();
